import React, { Fragment, useEffect, useState } from 'react';
import { fetchQrSvg, qrAssetUrl, qrCacheKey } from './qrHelpers';

/**
 * Shows a code's QR image. A code that carries `qrPreview` (the stored PNG the list sends)
 * renders that file directly. Anything without it - or whose file fails to load - falls back
 * to `/apidam/qr/{qrCodeId}/svg`, which sits behind the auth header and so is pulled through
 * Axios rather than pointed at from an <img>.
 */
const QrPreview = ({ item, size = 96, className = '' }) => {
  const previewUrl = qrAssetUrl(item, 'png');
  const [imageFailed, setImageFailed] = useState(false);
  const [markup, setMarkup] = useState('');
  const [status, setStatus] = useState('loading');
  const cacheKey = qrCacheKey(item);
  const useStoredImage = Boolean(previewUrl) && !imageFailed;

  // a different code gets a fresh chance at its stored image
  useEffect(() => {
    setImageFailed(false);
  }, [previewUrl]);

  useEffect(() => {
    if (useStoredImage) return undefined;

    let isActive = true;

    if (!item?.qrCodeId) {
      setStatus('error');
      return undefined;
    }

    setStatus('loading');
    fetchQrSvg(item)
      .then((data) => {
        if (!isActive) return;
        setMarkup(data);
        setStatus(data ? 'done' : 'error');
      })
      .catch(() => {
        if (!isActive) return;
        setStatus('error');
      });

    return () => {
      isActive = false;
    };
  }, [cacheKey, item?.qrCodeId, useStoredImage]);

  return (
    <Fragment>
      <div
        className={`qr_code_preview_box ${className}`}
        style={{ width: size, height: size, minWidth: size }}
      >
        {useStoredImage ? (
          <div className="qr_code_preview_svg">
            <img
              src={previewUrl}
              alt="QR code"
              style={{ objectFit: 'contain' }}
              onError={() => setImageFailed(true)}
            />
          </div>
        ) : (
          <Fragment>
            {status === 'loading' && <div className="qr_code_preview_placeholder" />}
            {status === 'error' && <div className="qr_code_preview_error">QR unavailable</div>}
            {status === 'done' && (
              <div
                className="qr_code_preview_svg"
                // markup comes from the DAM API itself, same trust level as the rest of the module
                dangerouslySetInnerHTML={{ __html: markup }}
              />
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default QrPreview;
